import type {
  ComposeAppointment,
  ComposeBase,
  ComposeMessage,
  OfficeContext,
  OutlookMailbox,
  ReadAppointment,
  ReadMessage,
} from './types';

const MAILBOX_REQUIREMENT_SETS = [
  '1.16', // assuming the next unreleased version number.
  '1.15',
  '1.14',
  '1.13',
  '1.12',
  '1.11',
  '1.10',
  '1.9',
  '1.8',
  '1.7',
  '1.6',
  '1.5',
  '1.4',
  '1.3',
  '1.2',
  '1.1',
];

/** Gets the current context the code is running in. */
export function getContext(): OfficeContext {
  return Office.context;
}

/** Gets the current mailbox from the context. */
export function getMailbox(): OutlookMailbox {
  return Office.context.mailbox;
}

/**
 * Check if the specified requirement set is supported by the host Office application.
 * @param name - The requirement set name (e.g., "ExcelApi").
 * @param minVersion - The minimum required version (e.g., "1.4").
 */
export function isApiSupported(name: string, minVersion: string): boolean {
  return getContext().requirements.isSetSupported(name, minVersion);
}

export function getHighestSupportedRequirementSet() {
  for (let index = 0; index < MAILBOX_REQUIREMENT_SETS.length; index++) {
    const version = MAILBOX_REQUIREMENT_SETS[index];
    if (isApiSupported('Mailbox', version)) {
      return version;
    }
  }

  return 'unknown';
}

/** Returns true if the current view appears to be in Message Compose mode. */
export function isViewInComposeMode(): boolean {
  const { item } = getMailbox();
  return !!(item && !('sender' in item));
}

/** Returns true if the current view appears to be in Message Read mode. */
export function isViewInReadMode(): boolean {
  const { item } = getMailbox();
  return !!(item && 'sender' in item);
}

export function canSkipTokenForFiltering(): boolean {
  const { item } = getMailbox();
  if (!item) {
    // The item.from parameter requires version 1.7
    return isApiSupported('Mailbox', '1.7');
  }
  return !!(item && (item.to !== undefined || item.from !== undefined));
}

export function canSendEmailWithoutToken(item: ComposeBase): boolean {
  return isApiSupported('Mailbox', '1.15') && item.sendAsync !== undefined;
}

/** Determines if the incoming item is an appointment in compose mode. */
export function itemIsAppointmentCompose(
  item: {
    itemType: string;
    sender?: unknown;
  } | null,
): item is ComposeAppointment {
  if (item === null) {
    return false;
  }
  return !('sender' in item) && item.itemType === 'appointment';
}

/** Determines if the incoming item is an appointment in read mode. */
export function itemIsAppointmentRead(
  item: { itemType: string; sender?: unknown } | null,
): item is ReadAppointment {
  if (item === null) {
    return false;
  }
  return 'sender' in item && item.itemType === 'appointment';
}

/** Determines if the incoming item is a message in compose mode. */
export function itemIsMessageCompose(
  item: { itemType: string; sender?: unknown } | null,
): item is ComposeMessage {
  if (item === null) {
    return false;
  }
  return !('sender' in item) && item.itemType === 'message';
}

/** Determines if the incoming item is a message in read mode. */
export function itemIsMessageRead(item: { itemType: string; sender?: unknown } | null): item is ReadMessage {
  if (item === null) {
    return false;
  }
  return 'sender' in item && item.itemType === 'message';
}
